// contexts/AuthContext.js - Version JWT avec gestion admin
import React, { createContext, useState, useEffect } from 'react';
import AsyncStorageHelper from '../services/asyncStorageHelper';
import { config } from '../config';
import sessionService from '../services/sessionService';
import alertService from '../services/alertService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);

  useEffect(() => {
    checkStoredAuth();
  }, []);

  const checkStoredAuth = async () => {
    try {
      const jwt = await AsyncStorageHelper.getString('jwtToken');
      const email = await AsyncStorageHelper.getString('userEmail');
      const isAdmin = await AsyncStorageHelper.getItem('isAdmin', false);
      
      console.log('Checking stored JWT:', { hasJWT: !!jwt, hasEmail: !!email, isAdmin });
      
      if (jwt && email) {
        if (isJWTValid(jwt)) {
          setUser({ email, token: jwt, isAdmin });
          console.log('Valid JWT found, user logged in as:', isAdmin ? 'admin' : 'user');
        } else {
          console.log('JWT expired, clearing stored credentials');
          await clearStoredCredentials();
        }
      }
    } catch (e) {
      console.error('Failed to check stored auth:', e);
      await clearStoredCredentials();
    } finally {
      setIsLoading(false);
    }
  };

  const isJWTValid = (jwt) => {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch (error) {
      console.error('Error validating JWT:', error);
      return false;
    }
  };

  const getJWTPayload = (jwt) => {
    try {
      return JSON.parse(atob(jwt.split('.')[1]));
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  const clearStoredCredentials = async () => {
    await Promise.all([
      AsyncStorageHelper.removeItem('jwtToken'),
      AsyncStorageHelper.removeItem('userEmail'),
      AsyncStorageHelper.removeItem('sheetId'),
      AsyncStorageHelper.removeItem('isAdmin') // 🆕 Nettoyer statut admin
    ]);
  };

  const signIn = async (email, password, sheetId) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${config.API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, sheetId })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Authentication failed' }));
        setError(errorData.error || 'Authentication failed');
        return false;
      }
      
      const data = await response.json();
      
      if (!data.token || !data.email || data.tokenType !== 'JWT') {
        setError('Invalid response from server');
        return false;
      }

      if (!isJWTValid(data.token)) {
        setError('Received invalid token');
        return false;
      }
      
      // 🆕 Sauvegarder statut admin
      const saveResults = await Promise.all([
        AsyncStorageHelper.setItem('jwtToken', data.token),
        AsyncStorageHelper.setItem('userEmail', data.email),
        AsyncStorageHelper.setItem('sheetId', sheetId),
        AsyncStorageHelper.setItem('isAdmin', data.isAdmin || false)
      ]);
      
      if (saveResults.every(result => result)) {
        setUser({ 
          email: data.email, 
          token: data.token, 
          isAdmin: data.isAdmin || false 
        });
        
        const payload = getJWTPayload(data.token);
        console.log('JWT Login successful:', {
          email: data.email,
          isAdmin: data.isAdmin || false,
          deviceId: payload?.device_id,
          sessionId: payload?.session_id,
          expiresAt: new Date(payload?.exp * 1000).toLocaleString()
        });
        
        return true;
      } else {
        console.error('Failed to save JWT credentials');
        setError('Failed to save authentication data');
        return false;
      }
      
    } catch (e) {
      console.error('Sign in error:', e);
      setError(e.message || 'Network error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await Promise.all([
        AsyncStorageHelper.removeItem('jwtToken'),
        AsyncStorageHelper.removeItem('userEmail'),
        AsyncStorageHelper.removeItem('sheetId'),
        AsyncStorageHelper.removeItem('isAdmin'),
        AsyncStorageHelper.removeItem('competitions'),
        AsyncStorageHelper.removeItem('lastSelectedSection')
      ]);
      
      setUser(null);
      setSessionInfo(null);
      setError(null);
      
      console.log('JWT Logout successful');
    } catch (e) {
      console.error('Sign out error:', e);
      setUser(null);
      setSessionInfo(null);
    }
  };

  const getValidAccessToken = async () => {
    try {
      const jwt = await AsyncStorageHelper.getString('jwtToken');
      if (!jwt) {
        throw new Error('No JWT token');
      }
      
      if (!isJWTValid(jwt)) {
        console.log('JWT expired, need to re-authenticate');
        await signOut();
        throw new Error('Token expired');
      }
      
      return jwt;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw new Error('Authentication required');
    }
  };

  const checkAuthorization = async () => {
    if (!user) return false;
    
    try {
      const jwt = await getValidAccessToken();
      return isJWTValid(jwt);
    } catch (error) {
      return false;
    }
  };

  // 🆕 Vérifier si utilisateur est admin
  const isUserAdmin = () => {
    return user?.isAdmin === true;
  };

  // 🆕 Clear cache serveur (admin seulement)
  const clearServerCache = async () => {
    if (!isUserAdmin()) {
      throw new Error('Admin access required');
    }

    try {
      const token = await getValidAccessToken();
      const response = await fetch(`${config.API_BASE}/cache/clear`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Cache clear failed' }));
        throw new Error(errorData.error || 'Failed to clear server cache');
      }

      const result = await response.json();
      console.log('Server cache cleared:', result);
      return result;
    } catch (error) {
      console.error('Clear server cache error:', error);
      throw error;
    }
  };

  const { 
    sessionInfo: currentSession, 
    isChecking, 
    formatTimeRemaining 
  } = sessionService.useSessionMonitor(
    user ? getValidAccessToken : null,
    (hoursRemaining) => {
      console.log('JWT Session expiring soon:', hoursRemaining);
      const payload = getJWTPayload(user.token);
      alertService.showAlert(
        'Session Expiring',
        `Your session will expire in ${hoursRemaining.toFixed(1)} hours.\n\nDevice: ${payload?.device_id || 'Unknown'}\nPlease save your work.`,
        [{ text: 'OK' }]
      );
    },
    () => {
      console.log('JWT Session expired');
      alertService.showAlert(
        'Session Expired',
        'Your session has expired. You will be redirected to login.',
        [{ 
          text: 'OK', 
          onPress: async () => {
            await signOut();
            if (typeof window !== 'undefined') {
              window.location.reload();
            }
          }
        }]
      );
    }
  );

  useEffect(() => {
    if (currentSession !== sessionInfo) {
      setSessionInfo(currentSession);
    }
  }, [currentSession]);

  const getSessionDetails = async () => {
    if (!user || !getValidAccessToken) return null;
    
    try {
      const jwt = user.token;
      const payload = getJWTPayload(jwt);
      
      if (payload) {
        return {
          email: payload.email,
          sheetId: payload.sheetId,
          isAdmin: payload.isAdmin || false, // 🆕 Inclure statut admin
          deviceId: payload.device_id,
          sessionId: payload.session_id,
          issuedAt: new Date(payload.iat * 1000),
          expiresAt: new Date(payload.exp * 1000),
          timeRemaining: payload.exp - Math.floor(Date.now() / 1000),
          timeRemainingHours: (payload.exp - Math.floor(Date.now() / 1000)) / 3600,
          tokenType: 'JWT'
        };
      }
      
      return await sessionService.getSessionInfo(getValidAccessToken);
    } catch (error) {
      console.error('Failed to get JWT session details:', error);
      return null;
    }
  };

  const checkSession = async () => {
    if (!user || !getValidAccessToken) return null;
    
    try {
      const jwt = user.token;
      const payload = getJWTPayload(jwt);
      
      if (payload) {
        const now = Math.floor(Date.now() / 1000);
        const validity = {
          isValid: payload.exp > now,
          timeRemaining: Math.max(0, payload.exp - now),
          timeRemainingHours: Math.max(0, (payload.exp - now) / 3600),
          expiresAt: new Date(payload.exp * 1000)
        };
        
        setSessionInfo(validity);
        return validity;
      }
      
      return null;
    } catch (error) {
      console.error('Session check failed:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        sessionInfo,
        sessionTimeRemaining: formatTimeRemaining,
        signIn,
        signOut,
        checkAuthorization,
        getValidAccessToken,
        checkSession,
        getSessionDetails,
        isJWTValid,
        getJWTPayload,
        // 🆕 Nouvelles méthodes admin
        isUserAdmin,
        clearServerCache
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};